import type { Trip } from '@transp/shared'

/**
 * Tempo maximo, em segundos de tempo simulado, que a posicao de um onibus e MANTIDA
 * quando ha um buraco entre dois segmentos da mesma viagem.
 *
 * Os buracos vem da quebra por salto implausivel: quando o GPS teleporta, a linha e
 * cortada, e entre o fim de um segmento e o inicio do proximo o onibus nao tem posicao.
 * Sem manter, ele PISCA - some do mapa e volta - e a contagem do comboio erra no
 * momento exato em que ela importa.
 *
 * Manter e escolha declarada, nao medicao: durante o buraco nao sabemos onde o onibus
 * estava. Dois minutos e o limite porque a cadencia e de 60 s: acima disso seriam duas
 * amostras perdidas seguidas, e ai a incerteza e grande demais para fingir posicao.
 */
export const MAX_HOLD_S = 120

/** Posicao interpolada dentro de UM segmento, ou null se o instante esta fora dele. */
export function positionAt(trip: Trip, t: number): [number, number] | null {
	const times = trip.times
	const n = times.length
	if (n < 2 || t < times[0] || t > times[n - 1]) return null

	let lo = 0
	let hi = n - 1
	while (hi - lo > 1) {
		const mid = (lo + hi) >> 1
		if (times[mid] <= t) lo = mid
		else hi = mid
	}
	const span = times[hi] - times[lo]
	const f = span > 0 ? (t - times[lo]) / span : 0
	const [x0, y0] = trip.path[lo]
	const [x1, y1] = trip.path[hi]
	return [x0 + (x1 - x0) * f, y0 + (y1 - y0) * f]
}

export type JourneyPosition = {
	position: [number, number]
	line: string
	direction: number
	/** Verdadeiro quando a posicao foi mantida por falta de dado, nao medida. */
	held: boolean
}

/**
 * Posicao de uma VIAGEM (todos os seus segmentos) no instante t.
 *
 * A viagem e a unidade certa aqui: um onibus e um so, ainda que a linha desenhada esteja
 * partida em varios segmentos por causa de salto de GPS. Contar segmentos em vez de
 * viagens contaria o mesmo onibus duas vezes.
 */
export function journeyPositionAt(segments: Trip[], t: number): JourneyPosition | null {
	for (const seg of segments) {
		const p = positionAt(seg, t)
		if (p) return { position: p, line: seg.line, direction: seg.direction, held: false }
	}
	// Fora de todos os segmentos: se o instante cai num buraco curto ENTRE dois deles,
	// mantem a ultima posicao conhecida.
	let best: { seg: Trip; gap: number } | null = null
	for (const seg of segments) {
		const end = seg.times[seg.times.length - 1]
		const gap = t - end
		if (gap > 0 && gap <= MAX_HOLD_S && (!best || gap < best.gap)) best = { seg, gap }
	}
	if (!best) return null
	// So mantem se existe um segmento POSTERIOR: senao a viagem simplesmente acabou.
	const hasNext = segments.some((s) => s.times[0] > t)
	if (!hasNext) return null
	const path = best.seg.path
	return {
		position: path[path.length - 1],
		line: best.seg.line,
		direction: best.seg.direction,
		held: true,
	}
}
